import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StatusChip } from "../components/ui-bits";
import { AdminLeads, AdminAnalytics, AdminResearch, AdminUsers } from "../components/AdminExtras";
import { Flag, Plus, Check, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const PHASE_OPTIONS = ["pre_development", "land_site", "connectivity_evacuation", "construction", "pre_commissioning", "commissioning"];
const FIELD_OPTIONS = ["project_type", "connectivity", "voltage_kv", "dedicated_line", "line_involved", "bess_included", "land_type", "forest_land", "protected_area", "crosses_nh", "crosses_sh", "crosses_railway", "crosses_water", "airport_zone", "defence_nearby", "crosses_tl", "business_model", "state"];

const EMPTY_APPROVAL = { key: "", name: "", plain_summary: "", department: "", authority: "", statutory_basis: "", description: "", documents: [], timeline_days: 30, fees_note: "", expert_note: "", phase: "construction", dependencies: [], project_types: [], variants: [], sub_steps: [], always_conditional: false, conditional_reason: "", voltage_dependent: false, connectivity_dependent: false, flag: false, status: "approved" };
const EMPTY_RULE = { key: "", name: "", logic: "all", conditions: [{ field: "project_type", values: [] }], approvals: [], scope: "national", states: [], status: "approved" };

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-[#475569] mt-0.5">{hint}</p>}
    </div>
  );
}

const inputCls = "mt-1 w-full bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none";

function ApprovalEditor({ approval, onClose, onSaved }) {
  const isNew = !approval.key_locked;
  const [form, setForm] = useState({ ...EMPTY_APPROVAL, ...approval });
  const [variantsText, setVariantsText] = useState(JSON.stringify(approval.variants || [], null, 2));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    let variants;
    try {
      variants = JSON.parse(variantsText);
    } catch {
      toast.error("Variants JSON is invalid — fix the syntax and save again");
      return;
    }
    const payload = { ...form, variants, timeline_days: parseInt(form.timeline_days) || 30 };
    delete payload.key_locked; delete payload.version; delete payload.updated_at; delete payload.kb_status;
    try {
      if (isNew) await api.post("/admin/approvals", payload);
      else await api.put(`/admin/approvals/${form.key}`, payload);
      toast.success("Approval saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="approval-editor-dialog">
        <DialogHeader><DialogTitle className="font-serif">{isNew ? "New approval definition" : `Edit — ${form.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Key (unique id)"><input value={form.key} disabled={!isNew} onChange={(e) => set("key", e.target.value)} className={inputCls} data-testid="approval-key-input" /></Field>
            <Field label="Phase">
              <select value={form.phase} onChange={(e) => set("phase", e.target.value)} className={inputCls} data-testid="approval-phase-select">
                {PHASE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="approval-name-input" /></Field>
          <Field label="Plain summary (required — one sentence a non-regulatory PM understands)">
            <textarea value={form.plain_summary} onChange={(e) => set("plain_summary", e.target.value)} className={`${inputCls} h-16`} data-testid="approval-plain-summary-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department (grouping)"><input value={form.department} onChange={(e) => set("department", e.target.value)} className={inputCls} data-testid="approval-department-input" /></Field>
            <Field label="Default timeline (days)"><input type="number" value={form.timeline_days} onChange={(e) => set("timeline_days", e.target.value)} className={inputCls} data-testid="approval-timeline-input" /></Field>
          </div>
          <Field label="Issuing authority"><textarea value={form.authority} onChange={(e) => set("authority", e.target.value)} className={`${inputCls} h-14`} /></Field>
          <Field label="Statutory basis"><textarea value={form.statutory_basis} onChange={(e) => set("statutory_basis", e.target.value)} className={`${inputCls} h-14`} /></Field>
          <Field label="Documents (one per line)">
            <textarea value={(form.documents || []).join("\n")} onChange={(e) => set("documents", e.target.value.split("\n").filter(Boolean))} className={`${inputCls} h-24`} data-testid="approval-documents-input" />
          </Field>
          <Field label="Sub-steps / sequence (one per line, optional)">
            <textarea value={(form.sub_steps || []).join("\n")} onChange={(e) => set("sub_steps", e.target.value.split("\n").filter(Boolean))} className={`${inputCls} h-16`} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fees note"><input value={form.fees_note} onChange={(e) => set("fees_note", e.target.value)} className={inputCls} /></Field>
            <Field label="Dependencies (comma-separated keys)" hint="e.g., sec68, gna_ctu">
              <input value={(form.dependencies || []).join(", ")} onChange={(e) => set("dependencies", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inputCls} />
            </Field>
          </div>
          <Field label="Solarian expert note"><textarea value={form.expert_note} onChange={(e) => set("expert_note", e.target.value)} className={`${inputCls} h-16`} /></Field>
          <div className="flex flex-wrap gap-4 text-sm">
            {[["always_conditional", "Always conditional"], ["voltage_dependent", "Voltage-dependent"], ["connectivity_dependent", "Connectivity-dependent"], ["flag", "⚑ Needs my verification"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-[#0B1F3A]">
                <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`approval-${k}-checkbox`} /> {l}
              </label>
            ))}
          </div>
          {form.always_conditional && (
            <Field label="Conditional reason shown to users"><input value={form.conditional_reason} onChange={(e) => set("conditional_reason", e.target.value)} className={inputCls} /></Field>
          )}
          <Field label="Status">
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="approval-status-select">
              <option value="approved">Approved (Verified — Solarian)</option>
              <option value="ai_draft">AI Draft — Pending Expert Review</option>
            </select>
          </Field>
          <Field label="Path/state variants (advanced — JSON)" hint='Each variant: {"name","connectivity","states":[...],"authority","department","process_note","timeline_days","documents":[...]}'>
            <textarea value={variantsText} onChange={(e) => setVariantsText(e.target.value)} className={`${inputCls} h-32 font-mono text-xs`} data-testid="approval-variants-input" />
          </Field>
          <button onClick={save} className="w-full bg-[#1D4ED8] text-white rounded-md py-2.5 text-sm font-medium" data-testid="approval-save-btn">Save approval</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RuleEditor({ rule, approvals, onClose, onSaved }) {
  const isNew = !rule.key_locked;
  const newUid = () => Math.random().toString(36).slice(2, 10);
  const [form, setForm] = useState(() => {
    const f = { ...EMPTY_RULE, ...rule };
    return { ...f, conditions: (f.conditions || []).map((c) => ({ ...c, _uid: c._uid || newUid() })) };
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setCond = (i, patch) => {
    const conds = form.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c));
    set("conditions", conds);
  };

  const save = async () => {
    const payload = { ...form, conditions: form.conditions.map(({ _uid, ...c }) => c) };
    delete payload.key_locked; delete payload.updated_at;
    try {
      if (isNew) await api.post("/admin/rules", payload);
      else await api.put(`/admin/rules/${form.key}`, payload);
      toast.success("Rule saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rule-editor-dialog">
        <DialogHeader><DialogTitle className="font-serif">{isNew ? "New rule" : `Edit rule — ${form.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Key"><input value={form.key} disabled={!isNew} onChange={(e) => set("key", e.target.value)} className={inputCls} data-testid="rule-key-input" /></Field>
            <Field label="Name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="rule-name-input" /></Field>
          </div>
          <Field label="Condition logic" hint="ALL = every condition must match · ANY = at least one">
            <select value={form.logic} onChange={(e) => set("logic", e.target.value)} className={inputCls} data-testid="rule-logic-select">
              <option value="all">ALL conditions (AND)</option>
              <option value="any">ANY condition (OR)</option>
            </select>
          </Field>
          <Field label="Conditions" hint="Values comma-separated, e.g., yes — or solar_ground, wind. 'Unsure' answers fire as Conditional automatically.">
            <div className="space-y-2 mt-1">
              {form.conditions.map((c, i) => (
                <div key={c._uid} className="flex gap-2 items-center">
                  <select value={c.field} onChange={(e) => setCond(i, { field: e.target.value })} className="bg-white border border-[#0B1F3A]/20 rounded-md px-2 py-2 text-xs" data-testid={`rule-cond-field-${i}`}>
                    {FIELD_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <span className="text-xs text-[#475569]">in</span>
                  <input value={(c.values || []).join(", ")} onChange={(e) => setCond(i, { values: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    className="flex-1 bg-white border border-[#0B1F3A]/20 rounded-md px-2 py-2 text-xs" data-testid={`rule-cond-values-${i}`} />
                  <button onClick={() => set("conditions", form.conditions.filter((_, j) => j !== i))} className="text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
              <button onClick={() => set("conditions", [...form.conditions, { field: "project_type", values: [], _uid: newUid() }])} className="text-xs font-medium text-[#0B1F3A] underline" data-testid="rule-add-condition-btn">+ Add condition</button>
            </div>
          </Field>
          <Field label="Approvals triggered">
            <div className="grid grid-cols-2 gap-1 mt-1 max-h-44 overflow-y-auto border border-[#0B1F3A]/10 rounded-md p-2">
              {approvals.map((a) => (
                <label key={a.key} className="flex items-center gap-2 text-xs text-[#0B1F3A]">
                  <input type="checkbox" checked={form.approvals.includes(a.key)}
                    onChange={(e) => set("approvals", e.target.checked ? [...form.approvals, a.key] : form.approvals.filter((k) => k !== a.key))}
                    data-testid={`rule-approval-${a.key}`} />
                  {a.name.slice(0, 42)}
                </label>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Scope">
              <select value={form.scope} onChange={(e) => set("scope", e.target.value)} className={inputCls} data-testid="rule-scope-select">
                <option value="national">National</option>
                <option value="state">State-specific</option>
              </select>
            </Field>
            {form.scope === "state" && (
              <Field label="States (comma-separated)"><input value={(form.states || []).join(", ")} onChange={(e) => set("states", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inputCls} /></Field>
            )}
          </div>
          <Field label="Status">
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
              <option value="approved">Approved (fires in wizard)</option>
              <option value="ai_draft">AI Draft — Pending Expert Review</option>
            </select>
          </Field>
          <button onClick={save} className="w-full bg-[#1D4ED8] text-white rounded-md py-2.5 text-sm font-medium" data-testid="rule-save-btn">Save rule</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalsTab({ approvals, onNew, onEdit, onDelete, onApprove }) {
  return (
    <>
      <div className="flex justify-end mb-3">
        <button onClick={onNew} className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-sm font-medium" data-testid="new-approval-btn">
          <Plus size={14} /> New approval
        </button>
      </div>
      <div className="bg-white border border-[#0B1F3A]/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-[#0B1F3A]/10 text-left text-xs uppercase tracking-wider text-[#475569]">
              <th className="px-4 py-3">Approval</th><th className="px-4 py-3">Phase</th><th className="px-4 py-3">Days</th><th className="px-4 py-3">Variants</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">v</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {approvals.map((a) => (
              <tr key={a.key} className="border-b border-[#0B1F3A]/5 hover:bg-[#F5F8FF]/60" data-testid={`admin-approval-row-${a.key}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-[#0B1F3A]">{a.name} {a.flag && <Flag size={12} className="inline text-[#059669]" />}</p>
                  <p className="text-xs text-[#475569]">{a.department}</p>
                </td>
                <td className="px-4 py-3 text-xs text-[#475569]">{a.phase}</td>
                <td className="px-4 py-3 text-xs">{a.timeline_days}</td>
                <td className="px-4 py-3 text-xs">{a.variants?.length || 0}</td>
                <td className="px-4 py-3"><StatusChip status={a.status === "ai_draft" ? "ai_draft" : "verified"} /></td>
                <td className="px-4 py-3 text-xs text-[#475569]">{a.version || 1}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    {a.status === "ai_draft" && (
                      <button onClick={() => onApprove(a.key)} className="inline-flex items-center gap-1 text-xs font-medium bg-[#059669] text-white rounded px-2.5 py-1.5" data-testid={`approve-draft-${a.key}`}><Check size={12} /> Approve</button>
                    )}
                    <button onClick={() => onEdit(a)} className="text-xs font-medium border border-[#0B1F3A]/25 rounded px-2.5 py-1.5 text-[#0B1F3A]" data-testid={`edit-approval-${a.key}`}>Edit</button>
                    <button onClick={() => onDelete(a.key)} className="text-xs text-red-500 border border-red-200 rounded px-2 py-1.5" data-testid={`delete-approval-${a.key}`}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RulesTab({ rules, onNew, onEdit, onDelete }) {
  return (
    <>
      <div className="flex justify-end mb-3">
        <button onClick={onNew} className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-sm font-medium" data-testid="new-rule-btn">
          <Plus size={14} /> New rule
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {rules.map((r) => (
          <div key={r.key} className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4" data-testid={`admin-rule-row-${r.key}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-[#0B1F3A] text-sm">{r.name}</p>
              <div className="flex gap-1.5">
                <button onClick={() => onEdit(r)} className="text-xs font-medium border border-[#0B1F3A]/25 rounded px-2 py-1 text-[#0B1F3A]" data-testid={`edit-rule-${r.key}`}>Edit</button>
                <button onClick={() => onDelete(r.key)} className="text-xs text-red-500 border border-red-200 rounded px-2 py-1"><Trash2 size={12} /></button>
              </div>
            </div>
            <p className="text-xs text-[#475569] mt-2 font-mono">
              {r.conditions.map((c) => `${c.field} ∈ [${(c.values || []).join(", ")}]`).join(r.logic === "any" ? "  OR  " : "  AND  ")}
            </p>
            <p className="text-xs text-[#0B1F3A] mt-2">→ {r.approvals.join(", ")}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#475569] mt-2">{r.scope}{r.states?.length ? `: ${r.states.join(", ")}` : ""} · {r.status}</p>
          </div>
        ))}
      </div>
    </>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [rules, setRules] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editingRule, setEditingRule] = useState(null);

  const load = async () => {
    const [a, r] = await Promise.all([api.get("/admin/approvals"), api.get("/admin/rules")]);
    setApprovals(a.data);
    setRules(r.data);
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login?next=/admin"); return; }
    if (user.role !== "admin") return;
    load();
    api.get("/admin/coverage").then(({ data }) => setCoverage(data)).catch(() => {});
  }, [user, loading, navigate]);

  if (!loading && user && user.role !== "admin") return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      <p className="font-serif text-xl text-[#0B1F3A]">This area is for the Solarian admin team</p>
      <button onClick={() => navigate("/projects")} className="mt-4 bg-[#1D4ED8] text-white rounded-md px-5 py-2.5 text-sm font-medium">Go to my projects</button>
    </div>
  );

  const approveDraft = async (key) => {
    await api.put(`/admin/approvals/${key}/approve`);
    toast.success("Draft approved — it now fires in the public wizard");
    load();
  };

  const deleteApproval = async (key) => {
    if (!window.confirm("Delete this approval definition?")) return;
    await api.delete(`/admin/approvals/${key}`);
    load();
  };

  const deleteRule = async (key) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/admin/rules/${key}`);
    load();
  };

  const resetDemo = async () => {
    await api.post("/admin/reset-demo");
    toast.success("Demo account restored to seed state");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#0B1F3A]">Knowledge base</h1>
          <p className="text-sm text-[#475569] mt-1">The approval library and rules matrix behind every roadmap. Only Approved entries ever reach users. ⚑ = verify before relying on the value.</p>
        </div>
        <button onClick={resetDemo} className="inline-flex items-center gap-2 border border-[#0B1F3A]/30 rounded-md px-4 py-2 text-sm font-medium text-[#0B1F3A] hover:bg-[#0B1F3A]/5" data-testid="reset-demo-btn">
          <RotateCcw size={14} /> Reset demo account
        </button>
      </div>

      <Tabs defaultValue="approvals" className="mt-8">
        <TabsList data-testid="admin-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="approvals" data-testid="admin-tab-approvals">Approval library ({approvals.length})</TabsTrigger>
          <TabsTrigger value="rules" data-testid="admin-tab-rules">Rules matrix ({rules.length})</TabsTrigger>
          <TabsTrigger value="coverage" data-testid="admin-tab-coverage">Coverage</TabsTrigger>
          <TabsTrigger value="research" data-testid="admin-tab-research">AI research</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users (CRM)</TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals" className="mt-4">
          <ApprovalsTab approvals={approvals} onNew={() => setEditing({ ...EMPTY_APPROVAL })}
            onEdit={(a) => setEditing({ ...a, key_locked: true })} onDelete={deleteApproval} onApprove={approveDraft} />
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <RulesTab rules={rules} onNew={() => setEditingRule({ ...EMPTY_RULE })}
            onEdit={(r) => setEditingRule({ ...r, key_locked: true })} onDelete={deleteRule} />
        </TabsContent>

        <TabsContent value="coverage" className="mt-4">
          {!coverage ? <div className="h-40 bg-[#0B1F3A]/10 rounded-xl animate-pulse" /> : (
            <div className="space-y-4">
              <div className="bg-white border border-[#0B1F3A]/10 rounded-xl p-4">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#475569]">State-specific coverage (variants/rules present)</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {coverage.covered_states.map((s) => <span key={s} className="bg-[#0B1F3A] text-[#F5F8FF] rounded-full px-3 py-1 text-xs">{s}</span>)}
                </div>
                <p className="text-xs text-[#475569] mt-2">All other states fall back to the national baseline + "state steps being verified" banner.</p>
              </div>
              <div className="bg-white border border-[#0B1F3A]/10 rounded-xl overflow-x-auto" data-testid="coverage-matrix">
                <table className="w-full text-xs min-w-[900px]">
                  <thead>
                    <tr className="border-b border-[#0B1F3A]/10">
                      <th className="px-3 py-2 text-left text-[#475569]">Approval</th>
                      {coverage.scenarios.map((s) => <th key={s.label} className="px-2 py-2 text-[#475569] font-medium">{s.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {coverage.approvals.filter((a) => a.status === "approved").map((a) => (
                      <tr key={a.key} className="border-b border-[#0B1F3A]/5">
                        <td className="px-3 py-2 text-[#0B1F3A] font-medium">{a.name.slice(0, 45)} {a.flag && <Flag size={10} className="inline text-[#059669]" />}</td>
                        {coverage.scenarios.map((s) => (
                          <td key={s.label} className="px-2 py-2 text-center">{s.approvals.includes(a.key) ? <Check size={13} className="inline text-[#059669]" /> : <span className="text-[#0B1F3A]/15">—</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="research"><AdminResearch onDraftsCreated={load} /></TabsContent>
        <TabsContent value="users"><AdminUsers /></TabsContent>
        <TabsContent value="leads"><AdminLeads /></TabsContent>
        <TabsContent value="analytics"><AdminAnalytics /></TabsContent>
      </Tabs>

      {editing && <ApprovalEditor approval={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {editingRule && <RuleEditor rule={editingRule} approvals={approvals} onClose={() => setEditingRule(null)} onSaved={load} />}
    </div>
  );
}
